<script lang="ts">
	import { Bot, Mail } from 'lucide-svelte';
	import { SiFacebook, SiX } from '@icons-pack/svelte-simple-icons';
	import NewsletterCTA from '$lib/components/NewsletterCTA.svelte';
	import Turnstile from '$lib/components/Turnstile.svelte';

	let form: HTMLFormElement;
	let turnstileComponent: Turnstile;
	let turnstileToken: string | null = null;
	let submitting = false;
	let submitted = false;

	async function handleSubmit(event: Event) {
		event.preventDefault();
		if (!turnstileToken) {
			alert('Please complete the CAPTCHA verification');
			return;
		}

		submitting = true;
		const formData = new FormData(event.target as HTMLFormElement);
		formData.append('cf-turnstile-response', turnstileToken);

		try {
			const response = await fetch('https://formspree.io/f/movqlrjv', {
				method: 'POST',
				body: formData,
				headers: {
					Accept: 'application/json'
				}
			});

			if (response.ok) {
				submitted = true;
				(event.target as HTMLFormElement).reset();
				turnstileComponent.reset();
				turnstileToken = null;
			} else {
				alert('Failed to send message. Please try again.');
			}
		} catch (error) {
			console.error('Error sending message:', error);
			alert('An error occurred. Please try again later.');
		} finally {
			submitting = false;
		}
	}

	function handleTurnstileSuccess(event: CustomEvent<{ token: string }>) {
		turnstileToken = event.detail.token;
	}

	function handleTurnstileExpired() {
		turnstileToken = null;
	}

	function handleTurnstileError() {
		turnstileToken = null;
		alert('Error verifying CAPTCHA. Please try again.');
	}
</script>

<div class="container mx-auto px-4 py-8">
	<h1 class="mb-8 text-4xl font-bold text-blue-800 dark:text-blue-400">Contact Us</h1>

	<div class="grid gap-8 md:grid-cols-3">
		<!-- Contact Form -->
		<div class="rounded-lg bg-white p-6 shadow-lg md:col-span-2 dark:bg-gray-800">
			<h2 class="mb-6 flex items-center text-2xl font-semibold text-blue-700 dark:text-blue-400">
				<Mail class="mr-2 h-6 w-6" />
				Send us a message
			</h2>

			{#if submitted}
				<div class="py-8 text-center">
					<h3 class="mb-2 text-xl font-semibold text-green-600 dark:text-green-400">Thank You!</h3>
					<p class="text-gray-600 dark:text-gray-300">Your message has been sent successfully.</p>
					<button
						class="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
						on:click={() => (submitted = false)}
					>
						Send Another Message
					</button>
				</div>
			{:else}
				<form bind:this={form} on:submit={handleSubmit} class="space-y-4">
					<div>
						<label
							for="name"
							class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
						>
							Name
						</label>
						<input
							type="text"
							id="name"
							name="name"
							class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
							required
						/>
					</div>
					<div>
						<label
							for="email"
							class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
						>
							Email
						</label>
						<input
							type="email"
							id="email"
							name="email"
							class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
							required
						/>
					</div>
					<div>
						<label
							for="subject"
							class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
						>
							Subject
						</label>
						<input
							type="text"
							id="subject"
							name="subject"
							class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
							required
						/>
					</div>
					<div>
						<label
							for="message"
							class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
						>
							Message
						</label>
						<textarea
							id="message"
							name="message"
							rows="4"
							class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
							required></textarea>
					</div>

					<!-- Turnstile Widget -->
					<Turnstile
						bind:this={turnstileComponent}
						on:success={handleTurnstileSuccess}
						on:expired={handleTurnstileExpired}
						on:error={handleTurnstileError}
					/>

					<button
						type="submit"
						class="w-full rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
						disabled={submitting}
					>
						{#if submitting}
							Sending...
						{:else}
							Send Message
						{/if}
					</button>
				</form>
			{/if}
		</div>

		<!-- Social Links -->
		<div class="h-fit rounded-lg bg-white p-6 shadow-lg dark:bg-gray-800">
			<h2 class="mb-6 text-2xl font-semibold text-blue-700 dark:text-blue-400">Connect With Us</h2>

			<div class="space-y-4">
				<NewsletterCTA
					variant="sidebar"
					title="Stay Informed"
					description="Meeting announcements, event updates, and occasional newsletters."
				/>
				<a
					href="https://discord.gg/432vsVAuwZ"
					target="_blank"
					rel="noopener noreferrer"
					class="flex items-center rounded-lg bg-gray-50 p-4 transition-colors hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
				>
					<Bot class="h-8 w-8 text-[#5865F2]" />
					<span class="ml-4 font-medium">Join our Discord Server</span>
				</a>

				<a
					href="https://facebook.com/dosomethingarc"
					target="_blank"
					rel="noopener noreferrer"
					class="flex items-center rounded-lg bg-gray-50 p-4 transition-colors hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
				>
					<SiFacebook class="h-8 w-8 text-[#1877F2]" />
					<span class="ml-4 font-medium">Follow us on Facebook</span>
				</a>

				<a
					href="https://x.com/DoSomethingARC"
					target="_blank"
					rel="noopener noreferrer"
					class="flex items-center rounded-lg bg-gray-50 p-4 transition-colors hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
				>
					<SiX class="h-8 w-8 text-[#1DA1F2]" />
					<span class="ml-4 font-medium">Follow us on Twitter</span>
				</a>
			</div>
		</div>
	</div>
</div>
